// app.js

import {
    attachFieldValidationOnBlur,
    collectForm,
    validateInput,
    validateVisibilityByService,
} from './validators.js'

import {$, $e, $each, getElement} from '../../../core/cfjs/index.js'
import {$val} from "../helper.js";

import {lockPageScroll, unlockPageScroll} from '../scroll.js'

const SERVICE_DESCRIPTIONS = {
    vacation: `<strong>Vacation Rental:</strong> Turnover cleaning between guests — hotel-style details, restock on request, and a photo report.`,
    residential: `<strong>Residential:</strong> Routine or one-time home cleaning — kitchen, bathrooms, dusting, floors, and touch points.<br>Deep Cleaning available for first visits.`,
    moveout: `<strong>Move-In/Out Cleaning:</strong> Detail clean for an empty home after you move out. We tailor the estimate to your home size, condition, and layout.`,
    postconstruction: `<strong>Post-Construction:</strong> Final cleaning after remodel or new build. Includes interior dust removal, kitchens, bathrooms, floors, trim, and empty cabinets. Windows priced separately.`,
    commercial: `<strong>Commercial:</strong> Office and retail janitorial — 1–7× per week, pricing based on traffic level.`,
}

const CLEANING_LEVEL_DESC = {
    regular: '<b>Regular Cleaning</b> — best for routine maintenance: weekly, bi-weekly, monthly, or one-time.',
    deep_onetime: '<b>Deep Cleaning</b> — detailed one-time clean for first visits or homes needing extra attention. From $249.',
    deep_recurring: '<b>Deep + Regular</b> — Deep Cleaning for the first visit, then ongoing Regular Cleaning at your frequency.',
}

const INCLUDED_CONTENT = {
    regular: {
        title: 'Regular Cleaning',
        html: `<article class="pricing-level-card cleaning-tip-card">
            <h3>Regular Cleaning</h3>
            <p class="pricing-level-price">from <strong>$139</strong> / visit</p>
            <p class="pricing-level-lead">Best for routine maintenance: weekly, bi-weekly, monthly, or your preferred schedule.</p>
            <h4 class="pricing-level-subtitle">Includes:</h4>
            <ul class="pricing-level-list pricing-level-list--includes">
                <li>Kitchen surfaces, sink, faucet, stovetop exterior</li>
                <li>Bathroom cleaning: toilets, sinks, mirrors, showers/tubs</li>
                <li>Dusting of accessible surfaces</li>
                <li>Vacuuming carpets and rugs</li>
                <li>Sweeping and mopping hard floors</li>
                <li>Trash removal</li>
                <li>Main touch points: handles, switches, appliance handles</li>
            </ul>
            <h4 class="pricing-level-subtitle">Not included:</h4>
            <ul class="pricing-level-list pricing-level-list--excluded">
                <li>Detailed baseboards, inside oven, refrigerator, or cabinets</li>
                <li>Window tracks or detailed blinds</li>
                <li>Heavy grease, hard water buildup, or pet hair</li>
                <li>Moving heavy furniture or organizing personal items</li>
            </ul>
            <p class="pricing-level-hint">Add-ons available on the next step — many of these can be added to your visit.</p>
        </article>`,
    },
    deep: {
        title: 'Deep Cleaning',
        html: `<article class="pricing-level-card pricing-level-card--deep cleaning-tip-card cleaning-tip-card--deep">
            <h3>Deep Cleaning</h3>
            <p class="pricing-level-price">from <strong>$249</strong> / visit</p>
            <p class="pricing-level-lead">Best for first-time cleanings or homes that haven&apos;t been professionally cleaned in a while.</p>
            <h4 class="pricing-level-subtitle">Includes Regular Cleaning plus:</h4>
            <ul class="pricing-level-list pricing-level-list--includes">
                <li>Detailed baseboards</li>
                <li>Doors, trims, door frames, and touch points</li>
                <li>Cabinet fronts</li>
                <li>Inside oven and refrigerator when present and accessible</li>
                <li>Inside cabinets and drawers when accessible</li>
                <li>Detailed bathroom buildup cleaning</li>
                <li>Accessible vents, light covers, and ceiling fan areas</li>
                <li>Blinds and window sills</li>
                <li>Corners, edges, and areas behind doors</li>
            </ul>
            <p class="pricing-level-tip">Priced at 2× Regular base. Oven, fridge, cabinets, baseboards, vents &amp; blinds are included — not charged as add-ons.</p>
        </article>`,
    },
    deep_recurring: {
        title: 'Deep + Regular — How It Works',
        html: `<article class="pricing-level-card cleaning-tip-card">
            <h3>Deep + Regular Plan</h3>
            <p class="pricing-level-lead">A two-phase plan for homes that need a thorough start, then easy ongoing maintenance.</p>
            <h4 class="pricing-level-subtitle">Step 1 — First visit (Deep Cleaning)</h4>
            <p>Your first appointment is a full Deep Cleaning: detailed baseboards, inside oven and refrigerator when accessible, cabinet fronts, blinds, vents, extra bathroom detail, and more. Priced from <strong>$249</strong> for the first visit.</p>
            <h4 class="pricing-level-subtitle">Step 2 — Ongoing visits (Regular Cleaning)</h4>
            <p>After the deep clean, we switch to Regular Cleaning on the schedule you choose — weekly, bi-weekly, or monthly. Routine kitchen, bathrooms, dusting, floors, and touch points. From <strong>$139</strong> per visit, with frequency discounts on recurring service.</p>
            <h4 class="pricing-level-subtitle">What you&apos;ll see in your quote</h4>
            <p class="pricing-level-hint">Two separate prices: the one-time Deep Cleaning total for visit #1, plus your per-visit Regular rate for every visit after that.</p>
        </article>`,
    },
}

const MOVEOUT_LARGE_HOME_NOTE = 'Because of the size of your home, we may need to confirm a few details before your visit.';
const MOVEOUT_CONDITION_LABELS = {
    light: 'Light Move-In/Out',
    standard: 'Standard Move-In/Out',
    heavy: 'Heavy Move-In/Out',
};
const MOVEOUT_CONDITION_INCLUDES = {
    light: 'Empty home in good condition with light dust, normal appliances, and no heavy buildup.',
    standard: 'Full move-in/out clean including dusting, cabinets, bathrooms, kitchen, baseboards, and floors.',
    heavy: 'Deep move-in/out clean for homes with extra buildup, pet hair, grease, hard water, or neglected areas.',
};
const MOVEOUT_BASE_INCLUDES = [
    'Empty closets, cabinets, and drawers',
    'Kitchen, bathrooms, baseboards, and floors',
    'Window sills and accessible interior window frames',
    'Appliance exterior, microwave inside, fridge and oven when accessible',
];
const MOVEOUT_ADDON_LABELS = {
    windowsInterior: { label: 'Interior window glass', unit: 'pane' },
    windowTracks: { label: 'Window tracks', unit: 'window' },
    blindsDetailed: { label: 'Blinds cleaning', unit: 'each' },
    windowScreens: { label: 'Window screens', unit: 'each' },
};
const MOVEOUT_ADDON_KEYS = Object.keys(MOVEOUT_ADDON_LABELS);
const MOVEOUT_PROPERTY_LABELS = {
    house: 'House',
    condo: 'Apartment / Condo',
    cabin: 'Townhome / Duplex',
};

function formatMoveoutPropertyLine(formData = {}) {
    const parts = [];
    const propertyType = MOVEOUT_PROPERTY_LABELS[formData.propertyType] || formData.propertyType;
    if (propertyType) parts.push(propertyType);
    if (formData.sqft) parts.push(`${Number(formData.sqft).toLocaleString('en-US')} sq ft`);
    const br = Number(formData.br);
    const ba = Number(formData.ba);
    if (!Number.isNaN(br) && br > 0) parts.push(`${br} bedroom${br === 1 ? '' : 's'}`);
    if (!Number.isNaN(ba) && ba > 0) parts.push(`${ba} bathroom${ba === 1 ? '' : 's'}`);
    return parts.join(' · ');
}

function buildMoveoutIncludedSections(formData = {}) {
    const addons = formData.addons || {};
    const sections = [];
    const cleanliness = formData.cleanliness;

    if (cleanliness) {
        sections.push({
            title: MOVEOUT_CONDITION_LABELS[cleanliness] || 'Move-In/Out Cleaning',
            text: MOVEOUT_CONDITION_INCLUDES[cleanliness],
        });
    }

    const propertyLine = formatMoveoutPropertyLine(formData);
    if (propertyLine) {
        sections.push({
            title: 'Your property',
            text: propertyLine,
        });
    }

    sections.push({
        title: 'Included in this estimate',
        items: [...MOVEOUT_BASE_INCLUDES],
    });

    const selectedAddons = Object.entries(MOVEOUT_ADDON_LABELS)
        .map(([key, meta]) => {
            const qty = Number(addons[key] || 0);
            if (qty <= 0) return null;
            const unitLabel = qty === 1 ? meta.unit : `${meta.unit}s`;
            return `${meta.label} (${qty} ${unitLabel})`;
        })
        .filter(Boolean);

    if (selectedAddons.length) {
        sections.push({
            title: 'Add-ons in your estimate',
            items: selectedAddons,
        });
    } else {
        sections.push({
            text: 'Interior window glass, tracks, blinds, and screens are not included unless you added them in the calculator.',
        });
    }

    sections.push({
        text: 'If the home is dirtier than described, we may adjust the final price with you before cleaning begins.',
    });

    return sections;
}

function renderMoveoutIncludedSummary(scope, formData = {}) {
    const wrap = scope?.querySelector('.moveout-included-summary');
    const list = scope?.querySelector('.moveout-included-list');
    if (!wrap || !list) return;

    const sections = buildMoveoutIncludedSections(formData);
    list.innerHTML = sections.map((section) => {
        const title = section.title
            ? `<p class="moveout-included-title">${section.title}</p>`
            : '';
        const text = section.text
            ? `<p class="moveout-included-text">${section.text}</p>`
            : '';
        const items = section.items?.length
            ? `<ul class="moveout-included-items">${section.items.map((item) => `<li>${item}</li>`).join('')}</ul>`
            : '';
        return `<div class="moveout-included-block">${title}${text}${items}</div>`;
    }).join('');

    wrap.classList.remove('hidden');
}

function renderMoveoutAddonOffer(scope, formData = {}) {
    const wrap = scope?.querySelector('.moveout-addon-offer');
    const list = scope?.querySelector('.moveout-addon-offer-list');
    if (!wrap || !list) return;

    const addons = formData.addons || {};
    const available = Object.entries(MOVEOUT_ADDON_LABELS)
        .filter(([key]) => Number(addons[key] || 0) <= 0);

    if (!available.length) {
        list.innerHTML = '';
        wrap.classList.add('hidden');
        return;
    }

    list.innerHTML = available.map(([key, meta]) => {
        const id = `succ_${key}`;
        return `<div class="addon-row addon-row--counter">
            <span class="addon-counter-label">${meta.label}</span>
            <span class="number-field">
                <button type="button" class="stepper btn-stepper-left" data-target="${id}" data-dir="-1" aria-label="Decrease ${meta.label}">−</button>
                <input type="number" id="${id}" data-addon-key="${key}" min="0" step="1" value="0" inputmode="numeric"/>
                <button type="button" class="stepper btn-stepper-right" data-target="${id}" data-dir="1" aria-label="Increase ${meta.label}">+</button>
            </span>
        </div>`;
    }).join('');

    wrap.classList.remove('hidden');
}

function moveoutNeedsReview(formData = {}) {
    const sqft = Number(formData.sqft || 0);
    const br = Number(formData.br ?? 0);
    const ba = Number(formData.ba ?? 0);
    return sqft > 4500 || br > 5 || ba > 5;
}

function setMoveoutSuccessNotes(scope, formData = {}, breakdown = {}) {
    const reviewNote = scope?.querySelector('.moveout-review-note');
    const priceNote = scope?.querySelector('.success-price-note');
    const needsReview = breakdown.largeHomeReview || moveoutNeedsReview(formData);

    reviewNote?.classList.toggle('hidden', !needsReview);
    if (reviewNote && needsReview) {
        reviewNote.textContent = MOVEOUT_LARGE_HOME_NOTE;
    }

    renderMoveoutIncludedSummary(scope, formData);
    renderMoveoutAddonOffer(scope, formData);
    scope?.querySelector('.moveout-success-extras')?.classList.remove('hidden');
    priceNote?.classList.add('hidden');
}

function resetMoveoutSuccessUI(root) {
    const scope = $('.all-success', root);
    if (!scope) return;
    scope.querySelector('.moveout-review-note')?.classList.add('hidden');
    scope.querySelector('.moveout-included-summary')?.classList.add('hidden');
    scope.querySelector('.moveout-included-list') && (scope.querySelector('.moveout-included-list').innerHTML = '');

    scope.querySelector('.moveout-success-extras')?.classList.add('hidden');
    scope.querySelector('.moveout-addon-offer')?.classList.add('hidden');
    scope.querySelector('.moveout-addon-offer-list') && (scope.querySelector('.moveout-addon-offer-list').innerHTML = '');
    scope.querySelector('.moveout-recalc-btn')?.classList.add('hidden');
    scope.querySelector('.btn-book-url')?.classList.remove('hidden');

    scope.querySelector('.success-price-note')?.classList.remove('hidden');
}

const QUOTE_PRICE_MAX_FACTOR = 1.15

function formatQuoteRange(price) {
    const min = Math.ceil(Number(price))
    return {min: `$${min}`, max: ''}
}

function setConstructionBaseRowVisibility(scope, basePrice, totalPrice) {
    const row = scope?.querySelector('.construction-base-row')
    if (!row) return
    const show = Math.round(Number(basePrice)) !== Math.round(Number(totalPrice))
    row.classList.toggle('hidden', !show)
}

function setConstructionWindowRow(scope, { visible = false, price = null, pendingText = '' } = {}) {
    const row = scope?.querySelector('.construction-window-row')
    if (!row) return

    row.classList.toggle('hidden', !visible)

    const priceWrap = row.querySelector('.construction-window-price')
    const pendingEl = row.querySelector('.construction-window-pending')

    if (pendingText) {
        row.classList.add('is-pending')
        priceWrap?.classList.add('hidden')
        pendingEl?.classList.remove('hidden')
        if (pendingEl) pendingEl.textContent = pendingText
        return
    }

    row.classList.remove('is-pending')
    pendingEl?.classList.add('hidden')

    if (price != null && price > 0) {
        priceWrap?.classList.remove('hidden')
        applyQuoteRangeToElements(
            row.querySelector('.construction-window-min'),
            row.querySelector('.construction-window-max'),
            price,
        )
    } else {
        priceWrap?.classList.add('hidden')
    }
}

function resetConstructionSuccessUI(root) {
    const scope = $('.all-success', root)
    if (!scope) return
    scope.querySelector('.success-title').textContent = 'Your Estimate Is Ready!'
    scope.querySelector('.price-container--single')?.classList.remove('hidden')
    scope.querySelector('.price-container--construction')?.classList.add('hidden')
    scope.querySelector('.construction-floor-note')?.classList.add('hidden')
    scope.querySelector('.construction-window-note')?.classList.add('hidden')
    scope.querySelector('.construction-book-note')?.classList.add('hidden')
    scope.querySelector('.vacation-laundry-row')?.classList.add('hidden')
    scope.querySelector('.vacation-laundry-note')?.classList.add('hidden')
    resetMoveoutSuccessUI(root)
    setConstructionWindowRow(scope, { visible: false })
    scope.querySelector('.construction-base-row')?.classList.remove('hidden')
    const bookBtn = scope.querySelector('.btn-book-url')
    if (bookBtn) bookBtn.textContent = 'Book now'
}

function formatConstructionWindowPendingText(breakdown = {}, formData = {}) {
    const addons = formData?.addons || {}
    const windowCleaning = breakdown.windowCleaning || addons.windowCleaning || ''
    const panes = Number(breakdown.windowPanes ?? addons.windowPanes ?? 0)
    const scopeLabel = CONSTRUCTION_WINDOW_LABELS[windowCleaning] || 'Window cleaning'
    const paneSuffix = panes > 0 ? ` (${panes} panes)` : ''
    return `${scopeLabel}${paneSuffix} — price after access review`
}

function showPostConstructionSuccess(res, root, formData = {}) {
    const breakdown = res.data.calcBreakdown || {}
    const scope = $('.all-success', root)
    if (!scope) return

    const basePrice = breakdown.baseCleaning ?? res.data.calcPrice

    scope.querySelector('.price-container--single')?.classList.add('hidden')
    scope.querySelector('.price-container--dual')?.classList.add('hidden')
    scope.querySelector('.price-container--construction')?.classList.remove('hidden')

    applyQuoteRangeToElements(
        scope.querySelector('.construction-base-min'),
        scope.querySelector('.construction-base-max'),
        basePrice,
    )

    const bookBtn = scope.querySelector('.btn-book-url')
    const windowIncluded = breakdown.windowIncluded
        || ['interior', 'interior_exterior'].includes(formData?.addons?.windowCleaning)

    scope.querySelector('.construction-floor-note')?.classList.toggle('hidden', !breakdown.floorProtectionNote)
    scope.querySelector('.construction-book-note')?.classList.add('hidden')

    if (breakdown.windowAccessNote) {
        scope.querySelector('.success-title').textContent = 'Your Cleaning Estimate Is Ready'
        scope.querySelector('.construction-window-note')?.classList.toggle('hidden', !windowIncluded)
        setConstructionWindowRow(scope, {
            visible: windowIncluded,
            pendingText: formatConstructionWindowPendingText(breakdown, formData),
        })
        if (bookBtn) bookBtn.textContent = 'Book Cleaning'
        const totalPrice = res.data.calcPrice
        applyQuoteRangeToElements(
            scope.querySelector('.construction-total-min'),
            scope.querySelector('.construction-total-max'),
            totalPrice,
        )
        setConstructionBaseRowVisibility(scope, basePrice, totalPrice)
        return
    }

    scope.querySelector('.success-title').textContent = 'Your Estimate Is Ready!'
    scope.querySelector('.construction-window-note')?.classList.add('hidden')
    if (bookBtn) bookBtn.textContent = 'Book now'

    const windowPrice = Number(breakdown.windowPrice ?? 0)
    if (windowIncluded && windowPrice > 0) {
        setConstructionWindowRow(scope, { visible: true, price: windowPrice })
    } else {
        setConstructionWindowRow(scope, { visible: false })
    }

    const totalPrice = res.data.calcPrice
    applyQuoteRangeToElements(
        scope.querySelector('.construction-total-min'),
        scope.querySelector('.construction-total-max'),
        totalPrice,
    )
    setConstructionBaseRowVisibility(scope, basePrice, totalPrice)
}

function showCouponNote(res) {
    const el = $('.all-success .success-coupon-note')
    if (!el) return
    const before = res.data.priceWithoutCoupon
    if (!before || !res.data.couponLabel) {
        el.classList.add('hidden')
        return
    }
    const r = formatQuoteRange(before)
    el.innerHTML = `Promo code applied: <b>${res.data.couponLabel}</b> <s>${r.min}</s>`
    el.classList.remove('hidden')
}

function applyQuoteRangeToElements(minEl, maxEl, price) {
    const range = formatQuoteRange(price)
    if (minEl) minEl.textContent = range.min
    if (maxEl) {
        maxEl.textContent = ''
        maxEl.classList.add('hidden')
        maxEl.previousElementSibling?.classList.add('hidden')
    }
}

const TIP_MOBILE_MQ = window.matchMedia('(max-width: 768px)')
let activeTipKey = null
let tipCloseTimer = null

function isMobileTipView() {
    return TIP_MOBILE_MQ.matches
}

function cancelCloseTipPopover() {
    clearTimeout(tipCloseTimer)
    tipCloseTimer = null
}

function scheduleCloseTipPopover() {
    cancelCloseTipPopover()
    tipCloseTimer = setTimeout(() => {
        closeCleaningTipPopover()
    }, 180)
}

function mountCleaningTipLayers() {
    const popover = $('#cleaningTipPopover')
    const modal = $('#cleaningTipModal')
    if (popover && popover.parentElement !== document.body) {
        document.body.appendChild(popover)
    }
    if (modal && modal.parentElement !== document.body) {
        document.body.appendChild(modal)
    }
}

function getDetailsStep(service, visible) {
    if (service === 'moveout') return 5
    return visible[visible.length - 2]
}

function getNextButtonSteps(service, visible) {
    const detailsStep = getDetailsStep(service, visible)
    if (service === 'moveout') {
        return visible.filter((s) => s < detailsStep || s === 6)
    }
    return visible.filter((s) => s < detailsStep)
}

function getVisibleSteps(service) {
    if (service === 'residential') return [1, 2, 3, 4, 5, 6, 7]
    if (service === 'moveout') return [1, 2, 3, 6, 5, 7]
    if (service === 'postconstruction') return [1, 2, 3, 4, 7]
    return [1, 2, 3, 5, 6, 7]
}

function getAddonsStep(service) {
    if (service === 'postconstruction') return 4
    return 5
}

function getStepTitle(service, step) {
    switch (step) {
        case 1:
            return 'Choose Your Service'
        case 2:
            return 'Check Availability in Your Area'
        case 3:
            return 'Property Details'
        case 4:
            if (service === 'postconstruction') return 'Construction Details'
            return service === 'residential' ? 'Cleaning Level' : 'Optional Add-Ons'
        case 5:
            return service === 'moveout' ? 'Window Add-Ons' : (service === 'residential' ? 'Add-Ons' : 'Optional Add-Ons')
        case 6:
            return service === 'moveout' ? 'Cleaning Condition' : 'Cleaning Details'
        case 7:
            return service === 'postconstruction'
                ? 'Where Should We Send Your Estimate?'
                : 'Contact Info'
        default:
            return 'Contact Info'
    }
}

function getNextStep(service, step) {
    const steps = getVisibleSteps(service)
    const idx = steps.indexOf(step)
    return idx >= 0 && idx < steps.length - 1 ? steps[idx + 1] : step
}

function getPrevStep(service, step) {
    const steps = getVisibleSteps(service)
    const idx = steps.indexOf(step)
    return idx > 0 ? steps[idx - 1] : step
}

const ERR_ZIP =  'We currently don’t serve this area yet. But we’re expanding – feel free to <a href="/contact-us.html"><b><u>contact us</u></b></a>.'
const ZIP_CITY_MAP = {
    65721: "Ozark",
    65742: "Ozark / Rogersville / Fremont Hills / Selmore",
    65759: "Clever (Ozark/Nixa area)",
    65714: "Nixa",
    65802: "Springfield (Central / West)",
    65803: "Springfield (North)",
    65804: "Springfield (Southeast)",
    65807: "Springfield (Southwest)",
    65809: "Springfield (East)",
    65616: "Branson",
    65672: "Hollister",
    65680: "Kirbyville",
    65686: "Kimberling City",
    65737: "Branson West / Silver Dollar City",
    65739: "Ridgedale",
    65740: "Rockaway Beach",
    65681: "Lampe",
    65679: "Kirbyville (sub-area)",
    65653: "Forsyth",
    65771: "Walnut Shade",
    65738: "Republic",
    65619: "Battlefield",
    65631: "Clever",
    65669: "Highlandville",
    65753: "Sparta"
}


// ===== Errors / toasts
function clearErrors(scope) {
    if (!scope) return;

    $each('.error-msg', el => {
        el.textContent = ''
        el.classList.remove('shown')
    }, scope)

    $each("[aria-invalid='true']", el => {
        el.setAttribute('aria-invalid', 'false')
    })

}

function showFieldErrors(errMap = {}, scope) {
    if (!scope) return
    clearErrors(scope)

    for (const [field, msg] of Object.entries(errMap)) {
        const errEl = $(`.error-msg[data-error-for="${field}"]`, scope)
        let input = $(`#${field}`, scope)
        if (!input && field === 'cleanliness') {
            const svc = $val('service', '', scope)
            input = $(`#${getCleanlinessFieldId(svc)}`, scope)
        }
        if (!input && field === 'terms') {
            input = $('#terms-booking', scope)
        }
        if (!input && field === 'termsContactSms') {
            input = $('#modal-terms-sms', scope)
        }

        if (errEl) {
            if (!errEl.id) errEl.id = `err-${field}`
            errEl.innerHTML = msg
            errEl.classList.add('shown')
        }
        if (input) {
            input.setAttribute('aria-invalid', 'true')
            if (errEl) {
                const prev = (input.getAttribute('aria-describedby') || '')
                    .split(/\s+/)
                    .filter(Boolean)
                if (!prev.includes(errEl.id)) {
                    input.setAttribute('aria-describedby', [...prev, errEl.id].join(' '))
                }
            }
        }
    }

    scope.querySelector('.error-msg.shown')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
    })
}

function showToastMsg(text, kind = 'ok') {
    const toast = $('#toast')
    if (!toast) return
    toast.textContent = text
    toast.classList.remove('hidden', 'error', 'ok')
    toast.classList.add(kind === 'error' ? 'error' : 'ok')
    setTimeout(() => toast.classList.add('hidden'), 3000)
}

const hasErrors = (obj) => obj && Object.keys(obj).length > 0

const FIELD_ERROR_STEP = {
    service: 1,
    zip: 2,
    propertyType: 3,
    commercialType: 3,
    constructionFloors: 3,
    sqft: 3,
    br: 3,
    ba: 3,
    floorProtection: 4,
    windowCleaning: 4,
    windowPanes: 4,
    protectiveFilm: 4,
    cleaningLevel: 4,
    laundry: 6,
    laundryGuests: 6,
    cleanliness: 6,
    resFrequency: 6,
    commFrequency: 6,
    name: 7,
    phone: 7,
    email: 7,
    terms: 7,
    termsContactSms: 7,
}

function getStepForErrorField(field) {
    return FIELD_ERROR_STEP[field] ?? null
}

function showValidationErrors(errMap, scope, goToStepFn, currentStepNum) {
    if (!hasErrors(errMap)) return false

    const firstField = Object.keys(errMap)[0]
    const targetStep = getStepForErrorField(firstField)
    if (targetStep && targetStep !== currentStepNum && typeof goToStepFn === 'function') {
        goToStepFn(targetStep)
    }

    showFieldErrors(errMap, scope)
    return true
}


// ===== Templates
function mountTemplate(tplId, mountEl) {
    const tpl = $('#'+tplId)
    if (!tpl || !mountEl) return
    mountEl.innerHTML = ''
    mountEl.appendChild(tpl.content.cloneNode(true))
}

function clearDynamicMount(host) {
    if (!host) return
    host.innerHTML = ''
    delete host.dataset.mountedService
}

function isDynamicMountCurrent(host, service) {
    return !!(service && host?.dataset.mountedService === service && host.children.length > 0)
}

function getResidentialAddonsTplId(root) {
    const level = $('#cleaningLevel', root)?.value || 'regular'
    return level === 'regular' ? 'tpl-addons-residential' : 'tpl-addons-residential-deep'
}

function renderAddonsTpl(service, root) {
    const host = $('#addons', root)
    if (!host) return

    let mountKey = service
    let tplId = null

    if (service === 'residential') {
        tplId = getResidentialAddonsTplId(root)
        mountKey = `residential:${$('#cleaningLevel', root)?.value || 'regular'}`
    } else {
        const map = {
            vacation: 'tpl-addons-vacation',
            moveout: 'tpl-addons-moveout',
            commercial: 'tpl-addons-commercial',
        }
        tplId = map[service]
    }

    if (isDynamicMountCurrent(host, mountKey)) return
    host.innerHTML = ''

    if (tplId) {
        mountTemplate(tplId, host)
        host.dataset.mountedService = mountKey
        bindDynamicBounds(root)
        bindIncludedButtons(root)
    } else {
        delete host.dataset.mountedService
    }
}

function renderPostConstructionStepTpl(root) {
    const host = $('#postconstructionMount', root)
    const levelHost = $('#cleaningLevelMount', root)
    if (!host) return
    if (levelHost) {
        levelHost.innerHTML = ''
        delete levelHost.dataset.mountedService
    }
    if (isDynamicMountCurrent(host, 'postconstruction-step4')) return

    host.innerHTML = ''
    mountTemplate('tpl-step4-postconstruction', host)
    host.dataset.mountedService = 'postconstruction-step4'
    bindPostConstructionWindowFields(root)
}

function bindPostConstructionWindowFields(root) {
    const select = $('#windowCleaning', root)
    const wrap = $('#windowDetailsWrap', root)

    const applyWindowDetailsVisibility = (animate = false) => {
        const hide = (select?.value || 'none') === 'none'
        const update = () => wrap?.classList.toggle('hidden', hide)

        if (animate) {
            runWithBookingModalHeightAnimation(update)
        } else {
            update()
        }
    }

    if (select && !select.dataset.boundWindowToggle) {
        select.dataset.boundWindowToggle = '1'
        select.addEventListener('change', () => applyWindowDetailsVisibility(true))
    }

    applyWindowDetailsVisibility(false)
}

function renderCleaningLevelTpl(root) {
    const postHost = $('#postconstructionMount', root)
    if (postHost) {
        postHost.innerHTML = ''
        delete postHost.dataset.mountedService
    }
    const host = $('#cleaningLevelMount', root)
    if (!host) return
    const needsMount = !isDynamicMountCurrent(host, 'residential-level')

    if (needsMount) {
        host.innerHTML = ''
        mountTemplate('tpl-step4-residential-level', host)
        host.dataset.mountedService = 'residential-level'

        bindCards(root, {
        containerSelector: '#cleaningLevelCards',
        radioName: 'cleaningLevelCard',
        hiddenInputId: 'cleaningLevel',
        onChange: (level) => {
            clearDynamicMount($('#addons', root))
            updateResidentialDetailsUI(root, level)
        },
        desc: {
            map: CLEANING_LEVEL_DESC,
            hostId: 'cleaningLevelDesc',
            className: 'helper cleaning-level-desc choice-desc',
            createAfterSelector: '#cleaningLevelCards',
        },
        })
    }

    bindInfoTipTriggers(root)
}

function mountServiceSpecificByService(service, root) {
    const host = $('#step4Mount', root)
    if (!host) return
    if (isDynamicMountCurrent(host, service)) return

    host.innerHTML = ''

    const map = {
        vacation: 'tpl-step3-vacation',
        residential: 'tpl-step3-residential',
        moveout: 'tpl-step3-moveout',
        commercial: 'tpl-step3-commercial',
    }

    const tplId = map[service]
    if (!tplId) {
        delete host.dataset.mountedService
        return
    }

    mountTemplate(tplId, host)
    host.dataset.mountedService = service
    bindDynamicBounds(root)

    const valueOption = ['laundryInUnit', 'laundryOffsite'];
    const curLaundry = $('[name="booking_laundry"]:checked');
    if(curLaundry){
        const isLaundryGuests = valueOption.includes(curLaundry.value)
        $('.input-laundryGuests').classList.toggle('hidden', !isLaundryGuests);
    }


    // === RESIDENTIAL ===
    if (service === 'residential') {
        const level = $('#cleaningLevel', root)?.value || 'regular'

        bindCards(root, {
            containerSelector: '#frequencyCards',
            radioName: 'resFrequencyCard',
            hiddenInputId: 'resFrequency',
        })

        bindCards(root, {
            containerSelector: '#cleanlinessCards',
            radioName: 'cleanlinessCard',
            hiddenInputId: 'cleanliness',
            onChange: (val) => {
                updateResidentialDetailsUI(root, level)
                const notice = $('#heavyDeepNotice', root)
                notice?.classList.toggle('hidden', !(level === 'regular' && val === 'heavy'))
            },
            desc: {
                map: {
                    light: '<b>Light:</b> Lightly lived-in home, tidy, minimal buildup, cleaned regularly.',
                    standard: '<b>Standard:</b> Normal lived-in home with regular dust, bathroom use, kitchen use, and floors.',
                    heavy: '<b>Heavy:</b> Active household with kids, cooking, pets, visible dust, soap scum, grease, or extra buildup.',
                },
            },
        })

        updateResidentialDetailsUI(root, level)
    }

    // === MOVE-OUT ===
    if (service === 'moveout') {
        bindCards(root, {
            containerSelector: '#moveCleanlinessCards',
            radioName: 'moveCleanlinessCard',
            hiddenInputId: 'moveCleanliness',
            animateHeight: true,
            desc: {
                hostId: 'moveCleanlinessDesc',
                className: 'helper moveout-condition-desc desc-active',
                createAfterSelector: '#moveCleanlinessCards',
                map: {
                    light: '<b>Light Move-In/Out:</b> Empty home in good condition. Light dust, normal appliances, no heavy buildup.',
                    standard:
                        '<b>Standard Move-In/Out:</b> Normal move-in/out after typical living. Dust, cabinets, bathrooms, kitchen, baseboards, floors.',
                    heavy:
                        '<b>Heavy Move-In/Out:</b> Noticeably dirty home — more buildup, pet hair, grease, hard water, or neglected condition.',
                },
            },
        })
    }

    // === COMMERCIAL ===
    if (service === 'commercial') {
        bindCards(root, {
            containerSelector: '#commFrequencyCards',
            radioName: 'commFrequencyCard',
            hiddenInputId: 'commFrequency',
        })
    }
}

// ===== Numeric bounds helpers
function bindDynamicBounds(root) {
    setBoundsIfExists(root, 'windowsInterior', 0, 999, 1)
    setBoundsIfExists(root, 'windowTracks', 0, 999, 1)
    setBoundsIfExists(root, 'blindsDetailed', 0, 999, 1)
    setBoundsIfExists(root, 'windowScreens', 0, 999, 1)
    setBoundsIfExists(root, 'ownerLaundryRounds', 0, 50, 1)
    setBoundsIfExists(root, 'ownerLaundryMiles', 0, 200, 0.1)
    setBoundsIfExists(root, 'laundryBeds', 0, 30, 1)
    setBoundsIfExists(root, 'laundryTowels', 0, 300, 1)
    setBoundsIfExists(root, 'commCarpetSqft', 0, 40000, 1)
    setBoundsIfExists(root, 'commFloorSqft', 0, 40000, 1)
}

function closeCleaningTipPopover() {
    cancelCloseTipPopover()
    const popover = $('#cleaningTipPopover')
    popover?.classList.add('hidden')
    popover?.classList.remove('is-positioning')
    activeTipKey = null
}

function canFitCleaningTipPopover(anchor, pointer) {
    const popover = $('#cleaningTipPopover')
    if (!popover || popover.classList.contains('hidden') || !anchor) return false

    const margin = 12
    const gap = 12
    const popW = popover.offsetWidth
    const popH = popover.offsetHeight
    const vw = window.innerWidth
    const vh = window.innerHeight

    if (popW > vw - margin * 2 || popH > vh - margin * 2) {
        return false
    }

    const rect = anchor.getBoundingClientRect()
    const pointerX = pointer?.x ?? rect.right
    const pointerY = pointer?.y ?? rect.top

    const fitsAt = (left, top) => {
        if (left + popW > vw - margin) left = vw - popW - margin
        if (left < margin) left = margin
        if (top + popH > vh - margin) top = vh - popH - margin
        if (top < margin) top = margin
        return (
            top >= margin
            && left >= margin
            && top + popH <= vh - margin
            && left + popW <= vw - margin
        )
    }

    if (fitsAt(pointerX + gap, pointerY - 8)) return true
    return fitsAt(rect.left, rect.bottom + gap)
}

function positionCleaningTipPopover(anchor, pointer) {
    const popover = $('#cleaningTipPopover')
    if (!popover || !anchor) return

    popover.style.top = ''
    popover.style.left = ''

    const gap = 12
    const margin = 12
    const rect = anchor.getBoundingClientRect()
    const popW = popover.offsetWidth
    const popH = popover.offsetHeight

    const pointerX = pointer?.x ?? rect.right
    const pointerY = pointer?.y ?? rect.top

    const clampPosition = (left, top) => {
        if (left + popW > window.innerWidth - margin) {
            left = window.innerWidth - popW - margin
        }
        if (left < margin) left = margin
        if (top + popH > window.innerHeight - margin) {
            top = window.innerHeight - popH - margin
        }
        if (top < margin) top = margin
        return {left, top}
    }

    const fitsRight = pointerX + gap + popW <= window.innerWidth - margin
    let left
    let top

    if (fitsRight) {
        left = pointerX + gap
        top = pointerY - 8
    } else {
        left = rect.left
        top = rect.bottom + gap
    }

    ;({left, top} = clampPosition(left, top))

    popover.style.top = `${top}px`
    popover.style.left = `${left}px`
}

function openCleaningTipPopover(key, anchor, pointer) {
    mountCleaningTipLayers()
    const popover = $('#cleaningTipPopover')
    const content = INCLUDED_CONTENT[key]
    if (!popover || !content) return

    cancelCloseTipPopover()
    closeCleaningTipModal()

    $('#cleaningTipPopoverTitle').textContent = content.title
    $('#cleaningTipPopoverBody').innerHTML = content.html
    popover.classList.remove('hidden')
    popover.classList.add('is-positioning')

    requestAnimationFrame(() => {
        if (!canFitCleaningTipPopover(anchor, pointer)) {
            popover.classList.add('hidden')
            popover.classList.remove('is-positioning')
            openCleaningTipModal(key)
            return
        }
        positionCleaningTipPopover(anchor, pointer)
        popover.classList.remove('is-positioning')
        activeTipKey = key
    })
}

function openCleaningTipModal(key) {
    mountCleaningTipLayers()
    const modal = $('#cleaningTipModal')
    const content = INCLUDED_CONTENT[key]
    if (!modal || !content) return

    closeCleaningTipPopover()
    $('#cleaningTipTitle').textContent = content.title
    $('#cleaningTipBody').innerHTML = content.html
    modal.classList.remove('hidden')
    document.body.classList.add('tip-modal-open')
    lockPageScroll()
    activeTipKey = key
}

function closeCleaningTipModal() {
    const modal = $('#cleaningTipModal')
    if (!modal || modal.classList.contains('hidden')) return

    modal.classList.add('hidden')
    document.body.classList.remove('tip-modal-open')
    unlockPageScroll()
    if (!activeTipKey || $('#cleaningTipPopover')?.classList.contains('hidden')) {
        activeTipKey = null
    }
}

function toggleCleaningTip(key, anchor) {
    const content = INCLUDED_CONTENT[key]
    if (!content) return

    if (isMobileTipView()) {
        if (!$('#cleaningTipModal')?.classList.contains('hidden') && activeTipKey === key) {
            closeCleaningTipModal()
            return
        }
        openCleaningTipModal(key)
        return
    }

    if (!$('#cleaningTipPopover')?.classList.contains('hidden') && activeTipKey === key) {
        closeCleaningTipPopover()
        return
    }
    closeCleaningTipModal()
    openCleaningTipPopover(key, anchor)
}

function bindInfoTipTriggers(root) {
    $each('.level-info-link', (btn) => {
        if (btn.dataset.tipHoverBound) return
        btn.dataset.tipHoverBound = 'true'

        $e(btn, 'mouseenter', (e) => {
            if (isMobileTipView()) return
            openCleaningTipPopover(btn.dataset.tip, btn, {x: e.clientX, y: e.clientY})
        })

        $e(btn, 'mouseleave', () => {
            if (isMobileTipView()) return
            scheduleCloseTipPopover()
        })
    }, root)
}

function handleLevelInfoLinkClick(e) {
    const btn = e.target.closest('#booking-modal .level-info-link')
    if (!btn) return

    const key = btn.dataset.tip
    if (!key || !INCLUDED_CONTENT[key]) return

    e.preventDefault()
    e.stopPropagation()

    if (!$('#cleaningTipModal')?.classList.contains('hidden') && activeTipKey === key) {
        closeCleaningTipModal()
        return
    }

    openCleaningTipModal(key)
}

function initCleaningTipGlobalHandlers() {
    if (document.body.dataset.cleaningTipBound === 'true') return
    document.body.dataset.cleaningTipBound = 'true'

    mountCleaningTipLayers()
    document.addEventListener('click', handleLevelInfoLinkClick, true)

    const popover = $('#cleaningTipPopover')
    if (popover) {
        $e(popover, 'mouseenter', cancelCloseTipPopover)
        $e(popover, 'mouseleave', scheduleCloseTipPopover)
    }

    $e(document, 'click', (e) => {
        if (
            e.target.closest('.cleaning-tip-popover')
            || e.target.closest('.level-info-link')
            || e.target.closest('.cleaning-tip-modal')
        ) return
        closeCleaningTipPopover()
    })

    $e(document, 'keydown', (e) => {
        if (e.key !== 'Escape') return
        closeCleaningTipPopover()
        closeCleaningTipModal()
    })

    $e(window, 'resize', () => {
        if ($('#cleaningTipPopover')?.classList.contains('hidden')) return
        if (!activeTipKey) return
        const anchor = document.querySelector(`.level-info-link[data-tip="${activeTipKey}"]`)
        if (!anchor) return
        if (!canFitCleaningTipPopover(anchor)) {
            openCleaningTipModal(activeTipKey)
            return
        }
        positionCleaningTipPopover(anchor)
    })

    TIP_MOBILE_MQ.addEventListener('change', () => {
        closeCleaningTipPopover()
        closeCleaningTipModal()
    })
}

function bindIncludedButtons(root) {
    $each('.view-included-btn', (btn) => {
        if (btn.dataset.includedBound) return
        btn.dataset.includedBound = 'true'
        $e(btn, 'click', (e) => {
            e.preventDefault()
            openCleaningTipModal(btn.dataset.included)
        })
    }, root)
}

function updateResidentialDetailsUI(root, level) {
    const freqWrap = $('#frequencyWrap', root)
    const deepOne = $('#deepOneTimePlan', root)
    const deepRec = $('#deepRecurringPlan', root)
    const heavyNotice = $('#heavyDeepNotice', root)
    const freqLabel = $('#frequencyLabel', root)

    const isRegular = level === 'regular'
    const isDeepOnce = level === 'deep_onetime'
    const isDeepRec = level === 'deep_recurring'

    freqWrap?.classList.toggle('hidden', isDeepOnce)
    deepOne?.classList.toggle('hidden', !isDeepOnce)
    deepRec?.classList.toggle('hidden', !isDeepRec)

    if (freqLabel) {
        freqLabel.textContent = isDeepRec ? 'Ongoing Service Frequency*' : 'Frequency*'
    }

    const cleanliness = $('#cleanliness', root)?.value
    heavyNotice?.classList.toggle('hidden', !(isRegular && cleanliness === 'heavy'))
}

function setBoundsIfExists(root, id, min, max, step = 1) {
    const el = root.querySelector(`#${id}`)
    if (!el) return
    el.setAttribute('min', String(min))
    el.setAttribute('max', String(max))
    el.setAttribute('step', String(step))
}

// ===== Numeric + stepper helpers
function isCounterNumberInput(input) {
    return (
        input?.tagName === 'INPUT'
        && input.type === 'number'
        && !!(input.closest('.number-field') || input.closest('.sqft-input-wrap'))
    )
}

function normalizeCounterInputValue(input) {
    const min = Number(input.getAttribute('min') || '0')
    const max = Number(input.getAttribute('max') || '999999')
    const step = Number(input.getAttribute('step') || '1')
    let v = input.value.replace(',', '.').trim()

    if (v === '') {
        input.value = ''
        return
    }

    if (step % 1) {
        const num = Number(v)
        if (Number.isNaN(num)) {
            input.value = ''
            return
        }
        const clamped = Math.max(min, Math.min(max, num))
        input.value = String(Number(clamped.toFixed(1)))
        return
    }

    v = v.replace(/\D/g, '')
    if (v === '') {
        input.value = ''
        return
    }

    let num = parseInt(v, 10)
    if (Number.isNaN(num)) {
        input.value = ''
        return
    }

    num = Math.max(min, Math.min(max, num))
    input.value = String(num)
}

function bindCounterInputBehavior(root) {
    if (root.dataset.counterInputBound === 'true') return
    root.dataset.counterInputBound = 'true'

    $e(root, 'input', (e) => {
        const input = e.target
        if (!isCounterNumberInput(input)) return
        normalizeCounterInputValue(input)
        clearErrors(root)
    })

    $e(root, 'focusin', (e) => {
        const input = e.target
        if (!isCounterNumberInput(input)) return
        if (input.value === '0' || input.value === '0.0') {
            input.value = ''
        }
    })

    $e(root, 'blur', (e) => {
        const input = e.target
        if (!isCounterNumberInput(input)) return
        if (input.value === '' || Number.isNaN(Number(input.value))) {
            input.value = String(Number(input.getAttribute('min') || '0'))
            return
        }
        normalizeCounterInputValue(input)
    })
}

function bindStepperBounds(root, qid, id, min, max) {
    const input = qid(id)
    if (!input) return
    const step = Number(input.getAttribute('step') || '1')
    input.setAttribute('min', String(min))
    input.setAttribute('max', String(max))
    input.setAttribute('step', String(step))
}

function bindStepperDelegation(root) {
    if (root.dataset.stepperBound === 'true') return
    root.dataset.stepperBound = 'true'
    $e(root, 'click', (e) => {
        const btn = e.target.closest('button.stepper')
        if (!btn || !root.contains(btn)) return

        const rect = btn.getBoundingClientRect()
        if (
            e.clientX < rect.left ||
            e.clientX > rect.right ||
            e.clientY < rect.top ||
            e.clientY > rect.bottom
        ) {
            return
        }

        const id = btn.getAttribute('data-target')
        const dirAttr = btn.getAttribute('data-dir')
        if (!id || !dirAttr) return

        const input = $('#' + id)
        if (!input) return

        const step = Number(input.getAttribute('step') || '1')
        const min = Number(input.getAttribute('min') || '0')
        const max = Number(input.getAttribute('max') || '999999')

        const cur =
            input.value === '' ? NaN : Number(String(input.value).replace(',', '.'))
        const base = Number.isNaN(cur) ? (step % 1 ? 0 : min) : cur
        const dir = dirAttr === '-1' ? -1 : 1

        let next = base + dir * step
        next = Math.max(min, Math.min(max, next))
        input.value =
            step % 1 ? String(Number(next.toFixed(1))) : String(Math.round(next))
        input.dispatchEvent(new Event('input', {bubbles: true}))
    })
}

// ===== Cards
function triggerSelectionVibration() {
    try {
        navigator.vibrate?.(7)
    } catch {
        // Unsupported or blocked by browser policy
    }
}

function getCleanlinessFieldId(service) {
    if (service === 'moveout') return 'moveCleanliness'
    if (service === 'commercial') return 'commTraffic'
    return 'cleanliness'
}

function getCleanlinessValue(root, service) {
    const el = $(`#${getCleanlinessFieldId(service)}`, root)
    return el?.value || ''
}

const CARD_GROUP_BINDINGS = [
    ['.service-cards', 'serviceCard', 'service'],
    ['#propTypeCards', 'propertyTypeCard', 'propertyType'],
    ['#cleaningLevelCards', 'cleaningLevelCard', 'cleaningLevel'],
    ['#cleanlinessCards', 'cleanlinessCard', 'cleanliness'],
    ['#frequencyCards', 'resFrequencyCard', 'resFrequency'],
    ['#moveCleanlinessCards', 'moveCleanlinessCard', 'moveCleanliness'],
    ['#commFrequencyCards', 'commFrequencyCard', 'commFrequency'],
    ['#constructionFloorsCards', 'constructionFloorsCard', 'constructionFloors'],
]

const CONSTRUCTION_WINDOW_LABELS = {
    interior: 'Interior windows',
    interior_exterior: 'Interior + exterior windows',
}

function syncCardGroup(container, radioName, hidden, cardSelector = '.option-card') {
    if (!container || !hidden) return

    const checked = container.querySelector(`input[name="${radioName}"]:checked`)
    hidden.value = checked?.value ?? ''

    $each(cardSelector, (card) => {
        const r = $(`input[name="${radioName}"]`, card)
        const isOn = !!r && r.checked
        card.classList.toggle('is-selected', isOn)
        card.setAttribute('tabindex', '0')
        card.setAttribute('role', 'radio')
        card.setAttribute('aria-checked', isOn ? 'true' : 'false')
    }, container)
}

function shouldAnimateBookingModalHeight() {
    const modal = $('#booking-modal')
    return !!(
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches
        && modal?.classList.contains('open')
        && modal.querySelector('.modal-content')
    )
}

function lockBookingModalHeight(modalContent) {
    const fromHeight = modalContent.getBoundingClientRect().height
    modalContent.style.height = `${fromHeight}px`
    modalContent.style.overflow = 'hidden'
    return fromHeight
}

function releaseBookingModalHeight(modalContent) {
    if (!modalContent) return
    modalContent.style.height = ''
    modalContent.style.overflow = ''
}

function getBookingModalContentCap(modalContent) {
    if (!modalContent) return null
    const maxHeight = parseFloat(getComputedStyle(modalContent).maxHeight)
    return Number.isFinite(maxHeight) && maxHeight > 0 ? maxHeight : null
}

function animateBookingModalHeight(fromHeight, modalContent) {
    if (!modalContent || fromHeight == null) return

    modalContent.style.height = 'auto'
    const cap = getBookingModalContentCap(modalContent)
    let endHeight = modalContent.getBoundingClientRect().height
    if (cap != null) endHeight = Math.min(endHeight, cap)

    const cleanup = () => {
        modalContent.style.height = ''
        modalContent.style.overflow = ''
        modalContent.classList.remove('is-resizing')
        modalContent.removeEventListener('transitionend', onTransitionEnd)
        clearTimeout(fallbackTimer)
    }

    const onTransitionEnd = (e) => {
        if (e.target !== modalContent || e.propertyName !== 'height') return
        cleanup()
    }

    const fallbackTimer = setTimeout(cleanup, 500)

    if (Math.abs(endHeight - fromHeight) < 2) {
        cleanup()
        return
    }

    modalContent.classList.add('is-resizing')
    modalContent.style.height = `${fromHeight}px`
    modalContent.getBoundingClientRect()
    modalContent.addEventListener('transitionend', onTransitionEnd)

    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            modalContent.style.height = `${endHeight}px`
        })
    })
}

function runWithBookingModalHeightAnimation(updateFn) {
    const modalContent = $('#booking-modal .modal-content')
    if (!modalContent || typeof updateFn !== 'function') return

    if (!shouldAnimateBookingModalHeight()) {
        updateFn()
        return
    }

    const fromHeight = lockBookingModalHeight(modalContent)
    updateFn()
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            animateBookingModalHeight(fromHeight, modalContent)
        })
    })
}

function syncAllCardGroups(root) {
    for (const [containerSelector, radioName, hiddenInputId] of CARD_GROUP_BINDINGS) {
        const container = $(containerSelector, root)
        const hidden = $(`#${hiddenInputId}`, root)
        if (!container || !hidden) continue
        syncCardGroup(container, radioName, hidden)
    }
}

function bindCards(
    root,
    {
        containerSelector,
        radioName,
        hiddenInputId,
        onChange,
        desc,
        cardSelector = '.option-card',
        animateHeight = false,
    },
) {
    const qid = (id) => $(`#${id}`, root)
    const hidden = qid(hiddenInputId)
    const container = $(containerSelector, root)
    if (!hidden || !container) return

    const syncDescHolder = (holder, value) => {
        if (!holder || !desc?.map) return
        holder.innerHTML = value ? desc.map[value] || '' : ''
        holder.classList.toggle('visible', !!value)
        if (desc.singleLineValues?.length) {
            holder.classList.toggle('desc-one-line', desc.singleLineValues.includes(value))
        } else {
            holder.classList.remove('desc-one-line')
        }
    }

    const bindKey = `${radioName}:${hiddenInputId}`
    if (container.dataset.cardsBound === bindKey) {
        if (hidden.value) {
            const preset = container.querySelector(
                `input[name="${radioName}"][value="${CSS.escape(hidden.value)}"]`,
            )
            if (preset) preset.checked = true
        }
        syncCardGroup(container, radioName, hidden, cardSelector)
        if (desc) {
            syncDescHolder(qid(desc.hostId || `${hiddenInputId}Desc`), hidden.value)
        }
        return
    }
    container.dataset.cardsBound = bindKey

    const descHost = (function ensureDescHost() {
        if (!desc) return null
        const {
            hostId = `${hiddenInputId}Desc`,
            className = 'helper',
            createAfterSelector = containerSelector,
            activeClass = 'desc-active',
        } = desc

        let holder = qid(hostId)
        if (!holder) {
            const afterEl = root.querySelector(createAfterSelector) || container
            const anchor = afterEl?.nextElementSibling
            holder = document.createElement('div')
            holder.id = hostId
            holder.className = className
            if (anchor) {
                anchor.after(holder)
            } else {
                afterEl?.appendChild(holder)
            }
        }

        if (!holder.classList.contains(activeClass)) {
            holder.classList.add(activeClass)
        }

        return holder
    })()

    const updateSelected = () => {
        syncCardGroup(container, radioName, hidden, cardSelector)

        if (descHost) {
            syncDescHolder(descHost, hidden.value)
        }
    }

    const applySelection = (radio) => {
        if (!radio || radio.disabled) return
        if (radio.checked && hidden.value === radio.value) return

        container.querySelectorAll(`input[name="${radioName}"]`).forEach((inp) => {
            inp.checked = inp === radio
        })
        radio.dispatchEvent(new Event('change', {bubbles: true}))
    }

    $e(container, 'click', (e) => {
        const card = e.target.closest(cardSelector)
        if (!card || !container.contains(card)) return

        if (e.target.matches('input[type="radio"], input[type="checkbox"]')) return
        if (e.target.closest('.info-tip-trigger') || e.target.closest('.level-info-link')) return

        applySelection($(`input[name="${radioName}"]`, card))
    })

    $e(container, 'keydown', (e) => {
        if (e.key !== ' ' && e.key !== 'Enter') return
        const card = e.target.closest(cardSelector)
        if (!card) return
        e.preventDefault()
        applySelection($(`input[name="${radioName}"]`, card))
    })

    $e(container, 'change', (e) => {
        const t = e.target
        if (!(t instanceof HTMLInputElement) || t.name !== radioName) return
        const prevValue = hidden.value
        if (animateHeight) {
            runWithBookingModalHeightAnimation(updateSelected)
        } else {
            updateSelected()
        }
        if (hidden.value !== prevValue) {
            triggerSelectionVibration()
        }
        hidden.dispatchEvent(new Event('change', {bubbles: true}))
        clearErrors(root)
        onChange?.(hidden.value)
    })

    if (hidden.value) {
        const preset = container.querySelector(
            `input[name="${radioName}"][value="${CSS.escape(hidden.value)}"]`,
        )
        if (preset) preset.checked = true
        else hidden.value = ''
    }

    updateSelected()
}

// ===== Hide Cabin/Lodge for Move-in / Move-out =====
function hideCabinForMoveout(root, qid) {
    const svc = qid('service')?.value || ''
    const cabinCard = root
        .querySelector('input[name="propertyTypeCard"][value="cabin"]')
        ?.closest('.option-card')
    if (!cabinCard) return

    const shouldHide = svc === 'moveout'
 //   cabinCard.classList.toggle('hidden', shouldHide)
   // cabinCard.setAttribute('aria-hidden', shouldHide ? 'true' : 'false')

    const hiddenInput = qid('propertyType')
    if (shouldHide && hiddenInput?.value === 'cabin') {
        hiddenInput.value = ''
        root
            .querySelectorAll('input[name="propertyTypeCard"]')
            .forEach((r) => (r.checked = false))
    }
}

const COVERED_ZIPS = new Set(Object.keys(ZIP_CITY_MAP))



$e('[name="booking_coupon"]', 'change', (e) => {

    try {
        const $err =   $('[data-error-for="coupon"]');
        const $suc =  $('[data-success-for="coupon"]');
        $suc.innerHTML = '';
        $err.innerHTML = '';
        const code = (e.target.value || '').trim();
        if (!code) return;
        fetch('https://api.ozarks-cleaning.com/coupons/valid/'+encodeURIComponent(code), {
            method: "GET",
            headers: {
                'Content-Type': 'application/json'
            },
        }).then(e => e.json()).then((res) => {


            if (res.status) {
                $suc.innerHTML = res.data.message;

            } else {
                $err.innerHTML = res.data.errors[0];

            }
        }).catch(err => {
            $('[data-error-for="coupon"]').innerHTML = 'Could not validate coupon.';
        })

    } catch (e) {
        showToastMsg('Request failed. Please try again later.', 'error')
    }
})
function refreshZipFeedback() {
    const input = $('[name="booking_zip"]')
    if (!input) return

    const raw = (input.value || '').replace(/\D/g, '').slice(0, 5)
    const isExists = COVERED_ZIPS.has(raw)
    const city = ZIP_CITY_MAP[raw]
    const successEl = $('[data-success-for="zip"]')
    const errorEl = $('[data-error-for="zip"]')

    if (isExists && city) {
        successEl.textContent = `Great news! We serve ${city}.`
        errorEl.innerHTML = ''
    } else {
        successEl.textContent = ''
        errorEl.innerHTML = raw.length >= 5 ? ERR_ZIP : ''
    }
}

function syncNextBtnDisabledState() {
    const nextBtn = $('#nextBtn')
    if (!nextBtn || nextBtn.classList.contains('hidden')) return

    const zipStepActive = !$('#multi-booking-form [data-step="2"]')?.classList.contains('hidden')
    if (zipStepActive) {
        const raw = ($('[name="booking_zip"]')?.value || '').replace(/\D/g, '')
        nextBtn.disabled = !COVERED_ZIPS.has(raw)
        return
    }

    nextBtn.disabled = false
}

function handleChange(e) {
    const {value, name, checked} = e.target;
    if (name === 'booking_vacationDeep' && checked) {
        $each('[type="checkbox"]', el => el.checked = checked, e.target.closest('.addon-group'))
    }
    if (name === 'booking_laundry') {
        const valueOption = ['laundryInUnit', 'laundryOffsite'];
        $('.input-laundryGuests').classList.toggle('hidden', !valueOption.includes(value));
    }
    if (name === 'commercialType') {
        $('[name="commercialTypeOther"]').classList.toggle('hidden', $val('commercialType') !== 'other');
    }

    if(name === 'booking_zip') {
        const raw = (value || '').replace(/\D/g, '').slice(0, 5)
        if (value !== raw) e.target.value = raw

        const isExists = COVERED_ZIPS.has(raw)
        const city = ZIP_CITY_MAP[raw]
        const successEl = $('[data-success-for="zip"]')
        const errorEl = $('[data-error-for="zip"]')

        refreshZipFeedback()
        syncNextBtnDisabledState()
    }
}

function resetAll(
    root,
    qid,
    validateVisibilityByService,
    updateProgressBar,
    state,
) {
    qid('calcForm')?.reset()
    qid('service') && (qid('service').value = '')
    qid('propertyType') && (qid('propertyType').value = '')
    qid('submitSuccess')?.classList.add('hidden')
    qid('calcForm')?.classList.remove('hidden')
    state.lastQuote = null

    const modalTitle = document.getElementById('booking-modal-title')
    if (modalTitle) modalTitle.classList.remove('hidden')

    qid('serviceDesc') && (qid('serviceDesc').innerHTML = '')
    qid('cleaningLevel') && (qid('cleaningLevel').value = '')
    qid('cleaningLevelDesc') && (qid('cleaningLevelDesc').innerHTML = '')
    clearDynamicMount(qid('addons'))
    clearDynamicMount(qid('cleaningLevelMount'))
    clearDynamicMount(qid('postconstructionMount'))
    clearDynamicMount(qid('step4Mount'))
    $('#cleaningLevelStep')?.classList.remove('is-postconstruction-step')
    resetConstructionSuccessUI(root)
    resetMoveoutSuccessUI(root)
    qid('notes') && (qid('notes').innerHTML = '')
    qid('priceText') && (qid('priceText').textContent = '$—')
    qid('timeText') && (qid('timeText').textContent = '—')
    qid('crewText') && (qid('crewText').textContent = '—')
    qid('priceNote') && (qid('priceNote').textContent = '')

    clearErrors(root)
    validateVisibilityByService('', root)
    updateProgressBar(1, root)

    root
        .querySelectorAll(
            'input[name="serviceCard"], input[name="propertyTypeCard"]',
        )
        .forEach((r) => (r.checked = false))

    root.querySelectorAll('.option-card.is-selected').forEach((el) => {
        el.classList.remove('is-selected')
        el.setAttribute('aria-checked', 'false')
    })
}

export function initForm(rootSel = '#multi-booking-form') {
    const root = getElement(rootSel)
    if (!root) return

    const qid = (id) => $(`#${id}`, root)

    const TOTAL_STEPS = 7
    let currentStep = 1
    let lastQuote = null

    const titleEl = $('#booking-modal-title')

    initCleaningTipGlobalHandlers()

    $each('#cleaningTipModal .cleaning-tip-close, #cleaningTipModal .cleaning-tip-close-bottom, #cleaningTipModal .cleaning-tip-overlay', (el) => {
        $e(el, 'click', closeCleaningTipModal)
    })

    // ===== Init
    initNumericInputs(root, qid)

    bindCards(root, {
        containerSelector: '.service-cards',
        radioName: 'serviceCard',
        hiddenInputId: 'service',
        onChange: (svc) => validateVisibilityByService(svc, root),
        desc: {
            map: SERVICE_DESCRIPTIONS,
            hostId: 'serviceDesc',
            className: 'helper service-desc choice-desc',
            createAfterSelector: '.service-cards',
        },
    })

    bindCards(root, {
        containerSelector: '#propTypeCards',
        radioName: 'propertyTypeCard',
        hiddenInputId: 'propertyType',
    })

    bindServiceChange()
    bindWizardNav()
    goToStep(1)

    // ===== Numeric + steppers
    function initNumericInputs(root, qid) {
        bindCounterInputBehavior(root)
        bindStepperDelegation(root, qid)

        bindStepperBounds(root, qid, 'br', 0, 14)
        bindStepperBounds(root, qid, 'ba', 1, 10)
        bindStepperBounds(root, qid, 'sqft', 0, 40000)

        const br = qid('br')
        const ba = qid('ba')
        if (br && (br.value === '' || Number(br.value) < 0)) br.value = '1'
        if (ba && (ba.value === '' || Number(ba.value) < 1)) ba.value = '1'

        validateVisibilityByService($val('service'), root)
        renderAddonsTpl('', root)
        mountServiceSpecificByService('', root)
    }

    // ===== Service change
    function bindServiceChange() {
        qid('service')?.addEventListener('change', (e) => {
            const svc = e.target.value
            validateVisibilityByService(svc, root)
            clearErrors(root)
            qid('cleaningLevel') && (qid('cleaningLevel').value = '')
            qid('cleaningLevelDesc') && (qid('cleaningLevelDesc').innerHTML = '')
            clearDynamicMount(qid('addons'))
            clearDynamicMount(qid('cleaningLevelMount'))
            clearDynamicMount(qid('postconstructionMount'))
            clearDynamicMount(qid('step4Mount'))
            $('#cleaningLevelStep')?.classList.toggle('is-postconstruction-step', svc === 'postconstruction')
        })
    }


    $e('#service', 'change', () => hideCabinForMoveout(root, qid))

    // ===== Wizard nav + Enter behavior
    function bindWizardNav() {

        // Enter = Next
        $e('#calcForm', 'keydown', (e) => {
            if (e.key !== 'Enter') return
            const tag = (e.target?.tagName || '').toLowerCase()
            if (tag === 'textarea') return
            const contactStep = $val('service') === 'residential' ? 7 : 7
            if (currentStep < contactStep - 1) {
                e.preventDefault()
                onNext()
            }
        })

        $e('#prevBtn', 'click', () => {
            const svc = $val('service')

            if (currentStep === 1) {
                $('#booking-modal .modal-close')?.click()
                return
            }

            goToStep(getPrevStep(svc, currentStep))
        })


        $e('#nextBtn', 'click', () => {
            onNext()
        })


        $e('#bookBtn', 'click', () => {
            const stepErrors = validateStep(currentStep)

            if (hasErrors(stepErrors)) {
                showFieldErrors(stepErrors, root)
                return
            }

            goToStep(getNextStep($val('service'), currentStep))
        })

        // Single submit point

        const formSubmit = $('#multi-booking-form form');
        if (!formSubmit.classList.contains('init-submit')) {
            formSubmit.classList.add('init-submit')
            $e(formSubmit, 'submit', (e) => {
                e.preventDefault()
                onBook()
            })
        }

        $e('#terms', 'change', (e) => {
            const errEl = $('.error-msg[data-error-for="terms"]', root)
            if (errEl && e.target.checked) {
                errEl.textContent = ''
                errEl.classList.remove('shown')
            }
        })

        $e(root, 'input', handleChange)


    }

    // ===== Steps / progress
    function goToStep(n) {
        const svc = $val('service')
        const visible = getVisibleSteps(svc)
        const contactStep = visible[visible.length - 1]
        const detailsStep = getDetailsStep(svc, visible)

        const modalContent = $('#booking-modal .modal-content')
        const addonsStep = getAddonsStep(svc)
        const nextStep = visible.includes(n) ? n : visible[0]

        const animateHeight = shouldAnimateBookingModalHeight()
        const fromHeight = animateHeight && modalContent
            ? lockBookingModalHeight(modalContent)
            : null

        const commercialType = $('.commercialType');
        commercialType.classList.toggle('hidden', svc !== 'commercial');

        $('#cleaningLevelStep')?.classList.toggle('hidden', svc !== 'residential' && svc !== 'postconstruction')
        $('#cleaningLevelStep')?.classList.toggle('is-postconstruction-step', svc === 'postconstruction')

        currentStep = nextStep
        modalContent?.classList.toggle(
            'is-addons-step',
            addonsStep != null && currentStep === addonsStep,
        )

        $each('.step', (s) => {
            s.classList.toggle('hidden', Number(s.dataset.step) !== currentStep)
        }, root)

        const prevBtn = $('#prevBtn')
        const nextBtn = $('#nextBtn')
        const bookBtn = $('#bookBtn')
        const submitBtn = $('#submitBtn')

        if (prevBtn) {
            prevBtn.classList.remove('hidden')
            prevBtn.removeAttribute('aria-hidden')
            prevBtn.textContent = 'Back'
            prevBtn.disabled = false
        }

        const preDetailsSteps = getNextButtonSteps(svc, visible)
        if (nextBtn) nextBtn.classList.toggle('hidden', !preDetailsSteps.includes(currentStep))
        if (bookBtn) bookBtn.classList.toggle('hidden', currentStep !== detailsStep)
        if (submitBtn) submitBtn.classList.toggle('hidden', currentStep !== contactStep)

        if (submitBtn) {
            submitBtn.textContent = svc === 'postconstruction' ? 'Get My Estimate' : 'Get My Quote'
        }

        syncNextBtnDisabledState()
        if (currentStep === 2) refreshZipFeedback()

        if (currentStep === 3 && svc === 'postconstruction') {
            bindCards(root, {
                containerSelector: '#constructionFloorsCards',
                radioName: 'constructionFloorsCard',
                hiddenInputId: 'constructionFloors',
            })
        }

        if (currentStep === 4 && svc === 'residential') renderCleaningLevelTpl(root)
        if (currentStep === 4 && svc === 'postconstruction') renderPostConstructionStepTpl(root)
        if (currentStep === getAddonsStep(svc)) renderAddonsTpl(svc, root)
        if (currentStep === 6) mountServiceSpecificByService(svc, root)

        $('.for-scroll', root)?.scrollTo({top: 0})

        clearErrors(root)
        updateProgressBar(currentStep)

        if (animateHeight && modalContent) {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    animateBookingModalHeight(fromHeight, modalContent)
                })
            })
        } else {
            releaseBookingModalHeight(modalContent)
        }

        if (currentStep === contactStep) {
            attachFieldValidationOnBlur(
                root,
                ['zip', 'date', 'name', 'phone', 'email', 'terms-booking'],
                collectForm,
                validateInput,
                {forBooking: true},
                showFieldErrors,
            )
        }
    }

    function updateProgressBar(step) {
        const bar = $('#progress')
        const fill = $('.progressbar-fill', root)
        const svc = $('#service')?.value || ''
        const visibleSteps = getVisibleSteps(svc)
        const total = visibleSteps.length
        const visibleIndex = visibleSteps.indexOf(step) + 1
        const progress = total > 1 ? (visibleIndex - 1) / (total - 1) : 0
        const pct = Math.pow(progress, 0.6) * 100

        if (titleEl) {
            titleEl.textContent = `Step ${visibleIndex}. ${getStepTitle(svc, step)}`
        }
        bar?.style.setProperty('--progress', `${pct}%`)
        if (fill) fill.style.width = `${pct}%`
        bar?.setAttribute('aria-valuemax', String(total))
        bar?.setAttribute('aria-valuenow', String(visibleIndex))
    }

    function onNext() {
        const stepErrors = validateStep(currentStep)

        if (hasErrors(stepErrors)) {
            return showFieldErrors(stepErrors, root)
        }

        goToStep(getNextStep($val('service'), currentStep))
    }

    function validateStep(step) {
        syncAllCardGroups(root)

        const svc = $val('service');
        //const brStr = $val('br');
        const baStr = $val('ba');
        const prop = $val('propertyType');
        const sqftVal = Number($val('sqft', '0'))
        const errors = {}

        const isCommercial = svc === 'commercial';
        const isVacation = svc === 'vacation';
        const isResidential = svc === 'residential';
        const isMoveout = svc === 'moveout'
        const isPostconstruction = svc === 'postconstruction'

        if (step === 1) {
            if (!svc) errors.service = 'Please select a service.'
            return errors
        }


        if (step === 2) {
            const $zip = ($('[name="booking_zip"]')?.value || '').replace(/\D/g, '')
            if (!COVERED_ZIPS.has($zip)) {
                errors['zip'] = ERR_ZIP
            }
            return errors
        }
        if (step === 3) {
            if (!isCommercial && !prop) {
                errors.propertyType = 'Please select your property type.'
            }
          //  const brNum = Number(brStr)
            const baNum = Number(baStr)
         //   const brEmpty = brStr === '' || Number.isNaN(brNum)
            const baEmpty = baStr === '' || Number.isNaN(baNum)

            if (isCommercial) {
               // if (brEmpty || brNum < 1) errors.br = 'Please enter bedrooms (min 1).'
                const propertyType = $val('commercialType')
                if(propertyType){
                    if(propertyType === '0' || propertyType === 'other' && $val('commercialTypeOther') === ''){
                        errors.commercialType = 'Please enter other commercial type.'
                    }
                }
            }
            if (!isVacation && !isPostconstruction && !isMoveout && (baEmpty || baNum < 1))
                errors.ba = 'Please enter bathrooms (min 1).'
            if (isMoveout) {
                const brNum = Number($val('br'))
                const brStr = $val('br')
                const brEmpty = brStr === '' || Number.isNaN(brNum)
                if (brEmpty || brNum < 1) errors.br = 'Please enter bedrooms (min 1).'
                if (baEmpty || baNum < 1) errors.ba = 'Please enter bathrooms (min 1).'
            }
            if (!sqftVal || sqftVal <= 0) {
                errors.sqft = 'Square footage is required.'
            } else if (sqftVal > 40000) {
                errors.sqft = 'Square footage cannot exceed 40 000 ft².'
            }
            if (isPostconstruction && !qid('constructionFloors')?.value) {
                errors.constructionFloors = 'Please select number of floors.'
            }
            return errors
        }

        if (step === 4 && isPostconstruction) {
            if (!qid('floorProtection')?.value) {
                errors.floorProtection = 'Please select floor protection.'
            }
            const windowCleaning = qid('windowCleaning')?.value || 'none'
            if (!windowCleaning) {
                errors.windowCleaning = 'Please select window cleaning option.'
            }
            if (windowCleaning === 'interior' || windowCleaning === 'interior_exterior') {
                const panes = Number(qid('windowPanes')?.value || 0)
                if (!panes || panes <= 0) {
                    errors.windowPanes = 'Number of panes is required.'
                }
                if (!qid('protectiveFilm')?.value) {
                    errors.protectiveFilm = 'Please select protective film option.'
                }
            }
            return errors
        }

        if (step === 4 && isResidential) {
            const level = qid('cleaningLevel')?.value
            if (!level) errors.cleaningLevel = 'Please select a cleaning level.'
            return errors
        }

        if (step === 6) {
            if (isVacation) {
                const cf = $('[name="booking_laundry"]:checked')
                if (!cf) errors.laundry = 'Please choose How should we handle laundry.'

                const valueOption = ['laundryInUnit', 'laundryOffsite'];
                const isLaundryGuests = valueOption.includes(cf.value)
                if(isLaundryGuests && Number($val('laundryGuests')) < 1){
                    errors.laundryGuests = 'Please enter total guest capacity  (min 1).'
                }
                $('.input-laundryGuests').classList.toggle('hidden', !isLaundryGuests);
            }
            if (isResidential || isMoveout || isCommercial) {
                const c = getCleanlinessValue(root, svc)
                if (!c) {
                    errors.cleanliness = isCommercial
                        ? 'Please select traffic level.'
                        : 'Please select cleanliness/condition.'
                }
            }
            if (isResidential) {
                const level = qid('cleaningLevel')?.value || 'regular'
                if (level !== 'deep_onetime') {
                    const rf = qid('resFrequency')?.value
                    if (!rf) errors.resFrequency = 'Please choose frequency.'
                }
            }
            if (svc === 'commercial') {
                const cf = $val('commFrequency')
                if (!cf) errors.commFrequency = 'Please choose frequency (per week).'
            }
            return errors
        }

        return errors
    }

    // ===== Move-out success: included summary, add-on offer, coupon, recalculate
    function applyMoveoutSuccess(res, data) {
        $('.all-success .price-container--single').classList.remove('hidden')
        $('.all-success .price-container--dual').classList.add('hidden')
        $('.all-success .price-container--construction')?.classList.add('hidden')
        applyQuoteRangeToElements(
            $('.all-success .price-value.min'),
            $('.all-success .price-value.max'),
            res.data.calcPrice,
        )
        setMoveoutSuccessNotes($('.all-success', root), data, res.data.calcBreakdown || {})
        if (res.data.bookURL) $('#multi-booking-form .btn-book-url').href = res.data.bookURL
        showCouponNote(res)
        syncMoveoutRecalcBtn()
    }

    function collectMoveoutRecalcData() {
        const data = collectForm(root)
        const scope = $('.all-success', root)
        scope?.querySelectorAll('.moveout-addon-offer-list input[data-addon-key]').forEach((el) => {
            data.addons[el.dataset.addonKey] = Number(el.value || 0)
        })
        const cb = scope?.querySelector('#moveoutHasCoupon')
        const couponEl = scope?.querySelector('[name="success_coupon"]')
        if (cb?.checked && couponEl?.value.trim()) {
            data.coupon = couponEl.value.trim()
        }
        return data
    }

    async function recalcMoveout() {
        const scope = $('.all-success', root)
        const btn = scope?.querySelector('.moveout-recalc-btn')
        if (btn?.hasAttribute('disabled')) return

        const data = collectMoveoutRecalcData()
        const prevLabel = btn?.textContent
        if (btn) {
            btn.setAttribute('disabled', 'true')
            btn.textContent = 'Recalculating…'
        }

        try {
            // Пересчитываем уже созданную заявку (по токену из ссылки бронирования), а не создаём новую.
            const bookHref = $('#multi-booking-form .btn-book-url')?.href || ''
            const token = bookHref.split('/').filter(Boolean).pop()
            const res = await fetch('https://api.ozarks-cleaning.com/order-recalc/' + encodeURIComponent(token), {
                method: 'POST',
                body: JSON.stringify(data),
                headers: {'Content-Type': 'application/json'},
            }).then((e) => e.json())

            if (res.status) {
                applyMoveoutSuccess(res, data)
                qid('submitSuccess')?.scrollTo({top: 0, behavior: 'smooth'})
                showToastMsg('Your estimate has been updated.')
            } else {
                showToastMsg('Could not recalculate. Please try again.', 'error')
            }
        } catch (e) {
            console.log(e)
            showToastMsg('Request failed. Please try again later.', 'error')
        } finally {
            if (btn) {
                btn.removeAttribute('disabled')
                btn.textContent = prevLabel || 'Recalculate'
            }
        }
    }

    function syncMoveoutRecalcBtn() {
        const scope = $('.all-success', root)
        if (!scope) return
        const recalcBtn = scope.querySelector('.moveout-recalc-btn')
        const bookBtn = scope.querySelector('.btn-book-url')
        // "Dirty" = user added an add-on that isn't reflected in the current price yet.
        const dirty = [...scope.querySelectorAll('.moveout-addon-offer-list input[data-addon-key]')]
            .some((el) => Number(el.value || 0) > 0)
        recalcBtn?.classList.toggle('hidden', !dirty)
        bookBtn?.classList.toggle('hidden', dirty)
    }

    function validateSuccessCoupon(e) {
        const scope = $('.all-success', root)
        const $err = scope?.querySelector('[data-error-for="success-coupon"]')
        const $suc = scope?.querySelector('[data-success-for="success-coupon"]')
        const code = (e.target.value || '').trim()
        if ($err) $err.innerHTML = ''
        if ($suc) $suc.innerHTML = ''
        if (!code) return

        fetch('https://api.ozarks-cleaning.com/coupons/valid/' + encodeURIComponent(code), {
            method: 'GET',
            headers: {'Content-Type': 'application/json'},
        }).then((r) => r.json()).then((res) => {
            if (res.status) {
                if ($suc) $suc.innerHTML = res.data.message
            } else if ($err) {
                $err.innerHTML = res.data.errors[0]
            }
        }).catch(() => {
            if ($err) $err.innerHTML = 'Could not validate coupon.'
        })
    }

    function bindMoveoutSuccessExtras() {
        const scope = $('.all-success', root)
        if (!scope || scope.dataset.extrasBound === 'true') return
        scope.dataset.extrasBound = 'true'

        const couponCb = scope.querySelector('#moveoutHasCoupon')
        const couponWrap = scope.querySelector('.moveout-coupon-input')
        const couponInput = scope.querySelector('[name="success_coupon"]')

        if (couponCb) {
            $e(couponCb, 'change', () => {
                couponWrap?.classList.toggle('hidden', !couponCb.checked)
                if (!couponCb.checked && couponInput) {
                    couponInput.value = ''
                    scope.querySelector('[data-error-for="success-coupon"]') && (scope.querySelector('[data-error-for="success-coupon"]').innerHTML = '')
                    scope.querySelector('[data-success-for="success-coupon"]') && (scope.querySelector('[data-success-for="success-coupon"]').innerHTML = '')
                }
                syncMoveoutRecalcBtn()
            })
        }

        if (couponInput) {
            $e(couponInput, 'change', validateSuccessCoupon)
        }

        $e(scope, 'input', (e) => {
            if (
                e.target.matches('.moveout-addon-offer-list input[data-addon-key]')
                || e.target.matches('[name="success_coupon"]')
            ) {
                syncMoveoutRecalcBtn()
            }
        })

        $e(scope, 'click', (e) => {
            if (e.target.closest('.moveout-recalc-btn')) {
                e.preventDefault()
                recalcMoveout()
            }
        })
    }

    bindMoveoutSuccessExtras()

    // ===== Submit / confirmation view
    async function onBook() {
        const stepErrors = validateStep(currentStep)
        if (hasErrors(stepErrors)) {
            return showFieldErrors(stepErrors, root)
        }


        const data = collectForm(root)
        const errs = validateInput(data, {forBooking: true})

        if (showValidationErrors(errs, root, goToStep, currentStep)) {
            return
        }

        const form = $('#calcForm');
        const errMsg = $('.form-error-msg', form);
        form.classList.add("loading");
        errMsg.textContent = '';

        const onError = (e) => {
            console.log(e);
            gtag('event', 'calc_send_error', {
                'email': data.email,
                data: data
            });
            errMsg.textContent = 'An error occurred. Your request was not submitted. Please try again later.';
            form.classList.remove("loading");
        }

        gtag('event', 'conversion', {
            'send_to': 'AW-17940661763/RS_YCLu9kfUbEIOM4-pC',
            'value': 1.0,
            'currency': 'USD'
        });


        try {
            fetch('https://api.ozarks-cleaning.com/orders', {
                method: "POST",
                body: JSON.stringify(data),
                headers: {
                    'Content-Type': 'application/json'
                },
            }).then(e => e.json()).then((res) => {
                if (res.status) {
                    gtag('event', 'calc_send_success', {
                        'email': data.email,
                        data: JSON.stringify(data),
                        order: res.data.order_id
                    });
                    const isDual = res.data.ongoingPrice != null
                    const svc = $val('service')
                    resetConstructionSuccessUI(root)
                    if (svc === 'postconstruction' && res.data.calcBreakdown) {
                        showPostConstructionSuccess(res, root, data)
                    } else if (svc === 'moveout') {
                        applyMoveoutSuccess(res, data)
                    } else {
                        $('.all-success .price-container--single').classList.toggle('hidden', isDual)
                        $('.all-success .price-container--dual').classList.toggle('hidden', !isDual)
                        $('.all-success .price-container--construction')?.classList.add('hidden')
                        if (isDual) {
                            applyQuoteRangeToElements(
                                $('.all-success .price-value.deep-min'),
                                $('.all-success .price-value.deep-max'),
                                res.data.calcPrice,
                            )
                            applyQuoteRangeToElements(
                                $('.all-success .price-value.ongoing-min'),
                                $('.all-success .price-value.ongoing-max'),
                                res.data.ongoingPrice,
                            )
                        } else {
                            const level = $val('cleaningLevel')
                            if (level === 'deep_onetime' && titleEl) titleEl.textContent = 'One-Time Deep Cleaning Estimate'
                            if (level === 'regular' && titleEl) titleEl.textContent = 'Regular Estimate'
                            applyQuoteRangeToElements(
                                $('.all-success .price-value.min'),
                                $('.all-success .price-value.max'),
                                res.data.calcPrice,
                            )
                        }

                        const offsiteLaundryFee = Number(res.data.calcBreakdown?.offsiteLaundryFee ?? 0)
                        if (svc === 'vacation' && offsiteLaundryFee > 0) {
                            $('.all-success .vacation-laundry-fee').textContent = '$' + Math.ceil(offsiteLaundryFee)
                            $('.all-success .vacation-laundry-row')?.classList.remove('hidden')
                            $('.all-success .vacation-laundry-note')?.classList.remove('hidden')
                        }
                    }
                    $('#multi-booking-form .btn-book-url').href = res.data.bookURL;
                    showCouponNote(res)
                    showSubmitted()
                    form.classList.remove("loading");
                } else {
                    onError()
                }
            }).catch(onError)
            // await fetch('/api/booking', { method: 'POST', headers: {'Content-Type':'application/json'}, body: JSON.stringify(data) });

        } catch (e) {
            console.log(e);
            showToastMsg('Request failed. Please try again later.', 'error')
        }
    }

    function showSubmitted() {
        qid('calcForm')?.classList.add('hidden')
        qid('submitSuccess')?.classList.remove('hidden')

       $('[data-error-for="coupon"]').textContent = '';
      $('[data-success-for="coupon"]').textContent = '';
        const isSpecialCommercial = (['Medical / Dental Office', 'Restaurant / Cafe', 'School / Daycare'].includes($val('commercialType')))

        $('#submitSuccess .commercial-success').classList.toggle('hidden', !isSpecialCommercial)
        $('#submitSuccess .all-success').classList.toggle('hidden', isSpecialCommercial)

        updateProgressBar(TOTAL_STEPS)
        if (titleEl) titleEl.textContent = 'Success'
        qid('prevBtn')?.classList.add('hidden')
        qid('nextBtn')?.classList.add('hidden')

        qid('bookBtn')?.classList.add('hidden')
        qid('submitBtn')?.classList.add('hidden')
        launchConfettiVanilla();
    }

    $each('#booking-modal .modal-close, .fx-close', (btn) => {
        $e(btn, 'click', () => {
            resetAll(root, qid, validateVisibilityByService, updateProgressBar, {
                lastQuote,
            })
            goToStep(1)
        })
    })
}


